import Box from "@mui/material/Box";
import { LatLngBoundsExpression, LatLngExpression } from "leaflet";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AttributionControl, CircleMarker, MapContainer, TileLayer, Tooltip as LeafletTooltip } from "react-leaflet";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { FitBounds } from "./TripMap";
import { MAP_TILE_LAYERS, MapTileId } from "@/constants/MapTiles";
import "leaflet/dist/leaflet.css";

export interface RefuelPoint {
  position: LatLngExpression;
  /** Time, amount and source, shown when hovering the marker. */
  label: string;
  /** Geotab's own detection is drawn hollow, a transaction filled. */
  detected: boolean;
}

interface Props {
  points: RefuelPoint[];
  tileId: MapTileId;
  height: number;
}

/** A single marker would otherwise zoom to the tile server's limit, where the ÖPNV layer has no tiles. */
const MAX_ZOOM = 16;

/** Small map with one marker per fuel transaction or detected fill-up, fitted to the markers. */
export function RefuelMap({ points, tileId, height }: Props) {
  const { t } = useTranslation();
  const layer = MAP_TILE_LAYERS.find(l => l.id === tileId) ?? MAP_TILE_LAYERS[0];
  const bounds = useMemo<LatLngBoundsExpression | null>(
    () => (points.length > 0 ? (points.map(p => p.position) as [number, number][]) : null),
    [points],
  );
  const color = AUDIT_TOKENS.accent;

  return (
    <Box sx={{ height, borderRadius: `${AUDIT_TOKENS.radius.small}px`, overflow: "hidden", "& .leaflet-container": { height: "100%", width: "100%", fontFamily: AUDIT_TOKENS.fontFamily } }}>
      <MapContainer center={[51.16, 10.45]} zoom={6} zoomControl={false} attributionControl={false} style={{ height: "100%", width: "100%" }} aria-label={t("logbookAudit.refuelMap.title")}>
        <AttributionControl prefix={false} />
        <TileLayer key={layer.id} url={layer.url} attribution={layer.attribution} maxZoom={layer.maxZoom} />
        {points.map(point => (
          <CircleMarker
            key={point.label}
            center={point.position}
            radius={7}
            pathOptions={{ color, fillColor: point.detected ? "#fff" : color, fillOpacity: 1, weight: 2, dashArray: point.detected ? "3 3" : undefined }}
          >
            <LeafletTooltip>{point.label}</LeafletTooltip>
          </CircleMarker>
        ))}
        <FitBounds bounds={bounds} maxZoom={MAX_ZOOM} />
      </MapContainer>
    </Box>
  );
}
